import React, { useState, useEffect } from 'react';
import { ArrowRight, Star, Award, Users, Zap, Check, Play, ShoppingBag, TrendingUp, ChevronDown, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { mockProducts, brandHistory, verticals, testimonials } from '../mock';
import api, { fetchProducts, subscribeNewsletter } from '../services/api';
import { toast } from 'sonner';
import ContactButton from '../components/ContactButton';
import ProspectCapsuleSection from '../components/ProspectCapsuleSection';

const Home = () => {
  const [email, setEmail] = useState('');
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const [products, setProducts] = useState({ clothing: [], sports: [] });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState({
    taylorSuit: null,
    valleyHigh: null,
    collegiateBomber: null
  });

  // Fetch products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const shopifyProducts = await fetchProducts();
        
        // Fetch featured products directly by handle for accurate data
        const [taylorSuitData, valleyHighData, collegiateBomberData] = await Promise.all([
          api.get('/shopify/products/taylor-suit').catch(() => null),
          api.get('/shopify/products/the-valley-high-top-sneaker').catch(() => null),
          api.get('/shopify/products/collegiate-bomber-university-blue').catch(() => null)
        ]);
        
        // Helper function to get price from product data
        const getProductPrice = (productData) => {
          if (!productData?.data?.product) return null;
          const product = productData.data.product;
          // If product has variants, get price from first variant
          if (product.variants && product.variants.length > 0) {
            return product.variants[0].price;
          }
          // Otherwise use direct price
          return product.price;
        };
        
        setFeaturedProducts({
          taylorSuit: taylorSuitData?.data?.product ? {
            ...taylorSuitData.data.product,
            handle: 'taylor-suit',
            price: getProductPrice(taylorSuitData)
          } : null,
          valleyHigh: valleyHighData?.data?.product ? {
            ...valleyHighData.data.product,
            handle: 'the-valley-high-top-sneaker',
            price: getProductPrice(valleyHighData)
          } : null,
          collegiateBomber: collegiateBomberData?.data?.product ? {
            ...collegiateBomberData.data.product,
            handle: 'collegiate-bomber-university-blue',
            price: getProductPrice(collegiateBomberData)
          } : null
        });
        
        // Separate products by tags or type
        const clothing = shopifyProducts.filter(p => 
          !p.tags.includes('sports') && !p.tags.includes('bywe')
        ).slice(0, 4);
        
        const sports = shopifyProducts.filter(p => 
          p.tags.includes('sports')
        ).slice(0, 4);
        
        // Use Shopify products if available, otherwise fall back to mock
        setProducts({
          clothing: clothing.length > 0 ? clothing : mockProducts.clothing,
          sports: sports.length > 0 ? sports : mockProducts.sports
        });
      } catch (error) {
        console.error('Failed to load products:', error);
        // Fall back to mock data on error
        setProducts(mockProducts);
        toast.error('Could not load products. Showing sample products.');
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
  }, []);

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      setSubscribing(true);
      await subscribeNewsletter(email);
      toast.success('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      {/* Full-Screen Hero Section - Tunnel Walk */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Background Image with Cinematic Treatment */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_scholar-sprint-hub/artifacts/pl8gksy0_IMG_6097.jpeg" 
            alt="PRSVR - The Tunnel Walk" 
            className="w-full h-full object-cover object-center"
          />
          {/* Cinematic Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30"></div>
          {/* Grid Overlay for Performance Brand Feel */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `
              linear-gradient(90deg, transparent 49.5%, rgba(180, 175, 163, 0.3) 49.5%, rgba(180, 175, 163, 0.3) 50.5%, transparent 50.5%),
              linear-gradient(0deg, transparent 49.5%, rgba(180, 175, 163, 0.3) 49.5%, rgba(180, 175, 163, 0.3) 50.5%, transparent 50.5%)
            `,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full container mx-auto px-6 flex flex-col justify-center pt-28 md:pt-32">
          <div className="max-w-2xl">
            {/* Main Title - Executive Typography */}
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white uppercase tracking-tighter leading-none mb-4">
              THE
            </h1>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white uppercase tracking-tighter leading-none mb-4">
              TUNNEL
            </h1>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-none mb-8" style={{ color: '#B4AFA3' }}>
              WALK
            </h1>
            
            {/* Tagline */}
            <p className="text-xl md:text-2xl text-gray-300 font-light mb-10 max-w-lg leading-relaxed">
              Where <span className="text-warmSage font-semibold">performance</span> meets <span className="text-warmSage font-semibold">presence</span>.
              Luxury apparel for those who keep going.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-6">
              <Button 
                className="bg-warmSage text-black font-black text-sm tracking-wider px-8 py-6 rounded-none hover:bg-white transition-all duration-300"
                onClick={() => window.location.href = '/collections/tunnel-vision'}
                data-testid="hero-tunnel-btn"
              >
                SHOP THE TUNNEL <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-warmSage text-warmSage font-black text-sm tracking-wider px-8 py-6 rounded-none hover:bg-warmSage hover:text-black transition-all duration-300"
                onClick={() => window.location.href = '/academy'}
                data-testid="hero-academy-btn"
              >
                THE ACADEMY
              </Button>
            </div>

            {/* Est. 2012 - Moved below buttons */}
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-12 bg-warmSage/50"></div>
              <span className="text-gray-500 font-bold text-sm tracking-[0.3em] uppercase">Est. 2012 • Los Angeles</span>
            </div>

            {/* Stats Bar */}
            <div className="flex items-center gap-8 pt-8 border-t border-warmSage/30">
              <div>
                <p className="text-3xl font-black text-warmSage">NYFW</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Featured</p>
              </div>
              <div className="h-10 w-px bg-warmSage/30"></div>
              <a 
                href="https://www.bloomberg.com/news/articles/2021-10-25/side-hustle-ideas-how-to-start-small-business-without-quitting-your-day-job?embedded-checkout=true" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-warmSage transition-colors"
              >
                <Zap className="h-5 w-5 text-warmSage" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">As Seen In</p>
                  <p className="text-sm font-black text-white">BLOOMBERG</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-bounce">
          <span className="text-xs text-gray-500 uppercase tracking-wider mb-2">Explore</span>
          <ChevronDown className="h-5 w-5 text-warmSage" />
        </div>
      </section>


      {/* Tunnel Access - Shoppable Hotspot Section - MOVED UP */}
      <ProspectCapsuleSection />

      {/* Brand Philosophy Section */}
      <section className="py-24 bg-black border-t border-warmSage/10">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-warmSage font-bold text-sm tracking-[0.3em] uppercase mb-4 block">The PRSVR Philosophy</span>
              <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-6">
                BUILT FOR<br/>
                <span style={{ color: '#B4AFA3' }}>THOSE WHO KEEP GOING</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Every piece is designed for the moment before the moment. The tunnel. The lobby. The arrival. 
                Where presence is performance and style is strategy.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="border border-warmSage/20 p-6">
                  <p className="text-3xl font-black text-warmSage mb-2">100%</p>
                  <p className="text-sm text-gray-500 uppercase tracking-wider">Independent</p>
                </div>
                <div className="border border-warmSage/20 p-6">
                  <p className="text-3xl font-black text-warmSage mb-2">LA</p>
                  <p className="text-sm text-gray-500 uppercase tracking-wider">Designed</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-warmSage/10 to-black border border-warmSage/20 p-12 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-6xl font-black text-warmSage mb-4">PRSVR</p>
                  <p className="text-2xl font-black text-white uppercase tracking-widest">MEANS</p>
                  <p className="text-4xl font-black text-white uppercase tracking-tight mt-2">KEEP GOING</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Story - Expandable Timeline */}
      <section id="about" className="py-24 bg-black border-t border-warmSage/10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-warmSage font-bold text-sm tracking-[0.3em] uppercase mb-4 block">THE JOURNEY</span>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white uppercase">FROM TECH STARTUP<br/><span style={{ color: '#B4AFA3' }}>TO LUXURY STATUS</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandHistory.highlights.slice(0, showFullTimeline ? brandHistory.highlights.length : 3).map((item, index) => (
              <Card key={index} className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="text-warmSage font-black text-4xl mb-4">{item.year}</div>
                  <h3 className="font-black text-xl tracking-tight uppercase text-white mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {item.description.replace('worn by Nicki Minaj', 'by athletes')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          {!showFullTimeline && (
            <div className="text-center mt-8">
              <Button 
                onClick={() => setShowFullTimeline(true)}
                className="bg-transparent border-2 border-warmSage text-warmSage font-black tracking-wider hover:bg-warmSage hover:text-black transition-all"
              >
                VIEW FULL JOURNEY <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Featured Collections */}
      <section id="fashion" className="py-24 bg-black border-t border-warmSage/10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
            <div>
              <span className="text-warmSage font-bold text-sm tracking-[0.3em] uppercase mb-4 block">PRSVR COLLECTION</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white uppercase">READY TO WEAR</h2>
              <p className="text-gray-500 mt-2">Performance-grade luxury with the power to be seen</p>
            </div>
            <Button 
              className="bg-transparent border-2 border-warmSage text-warmSage font-black tracking-wider hover:bg-warmSage hover:text-black transition-all"
              onClick={() => window.location.href = '/collections/in-stock'}
            >
              VIEW ALL <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Taylor Suit - Featured */}
            <Card 
              className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group md:col-span-2 overflow-hidden"
            >
              <div className="relative overflow-hidden cursor-pointer aspect-[16/9]" onClick={() => window.location.href = `/products/${featuredProducts.taylorSuit?.handle || 'taylor-suit'}`}>
                <img 
                  src={featuredProducts.taylorSuit?.images?.[0]?.url || "https://customer-assets.emergentagent.com/job_prsvr-uniforms/artifacts/i3qamr5f_IMG_3259.jpeg"}
                  alt={featuredProducts.taylorSuit?.title || "The Taylor Suit"}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="bg-warmSage text-black font-black tracking-wider">VIEW PRODUCT</Button>
                </div>
              </div>
              <CardContent className="p-8 bg-black">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-black text-white uppercase tracking-tight">{featuredProducts.taylorSuit?.title || "THE TAYLOR SUIT"}</h3>
                    <p className="text-gray-500 mt-2">Signature piece. Premium construction meets timeless design.</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black text-warmSage">${featuredProducts.taylorSuit?.price?.toFixed(2) || '300.00'}</p>
                    <Button size="sm" className="bg-warmSage text-black font-black mt-2 hover:bg-white transition-colors" onClick={(e) => { e.stopPropagation(); window.location.href = `/products/${featuredProducts.taylorSuit?.handle || 'taylor-suit'}`; }}>VIEW</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Valley High Sneakers */}
            <Card 
              className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group cursor-pointer overflow-hidden"
              onClick={() => window.location.href = `/products/${featuredProducts.valleyHigh?.handle || 'the-valley-high-top-sneaker'}`}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={featuredProducts.valleyHigh?.images?.[0]?.url || "https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/vyjmxr5z_IMG_2792.JPG"}
                  alt={featuredProducts.valleyHigh?.title || "Valley High Sneaker"}
                  className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="bg-warmSage text-black font-black tracking-wider">VIEW PRODUCT</Button>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-black/80 text-warmSage text-xs font-black px-3 py-1 uppercase tracking-wider border border-warmSage/30">Footwear</span>
                </div>
              </div>
              <CardContent className="p-8 bg-black">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{featuredProducts.valleyHigh?.title || "VALLEY HIGH SNEAKERS"}</h3>
                    <p className="text-gray-500 mt-2 text-sm">Elevated footwear designed for the streets.</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-warmSage">${featuredProducts.valleyHigh?.price?.toFixed(2) || '189.00'}</p>
                    <Button size="sm" className="bg-warmSage text-black font-black mt-2 hover:bg-white transition-colors">VIEW</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Collegiate Bomber */}
            <Card 
              className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group overflow-hidden"
            >
              <div className="relative overflow-hidden cursor-pointer" onClick={() => window.location.href = '/collections/the-collegiate-bomber'}>
                <img 
                  src={featuredProducts.collegiateBomber?.images?.[0]?.url || "https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/ygs0x6wd_IMG_2794.JPG"}
                  alt={featuredProducts.collegiateBomber?.title || "Collegiate Bomber"}
                  className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="bg-warmSage text-black font-black tracking-wider">VIEW PRODUCT</Button>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-black/80 text-warmSage text-xs font-black px-3 py-1 uppercase tracking-wider border border-warmSage/30">Archive</span>
                </div>
              </div>
              <CardContent className="p-8 bg-black">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">{featuredProducts.collegiateBomber?.title || "COLLEGIATE BOMBER"}</h3>
                    <p className="text-gray-500 mt-2 text-sm">From the archives. Limited availability.</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-warmSage">${featuredProducts.collegiateBomber?.price?.toFixed(2) || '944.00'}</p>
                    <Button size="sm" className="bg-warmSage text-black font-black mt-2 hover:bg-white transition-colors" onClick={(e) => { e.stopPropagation(); window.location.href = '/collections/the-collegiate-bomber'; }}>VIEW</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Collections Section */}
      <section className="py-32 bg-black relative overflow-hidden border-t border-warmSage/10">
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-warmSage font-bold text-sm tracking-[0.3em] uppercase mb-4 block">CURATED COLLECTIONS</span>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white uppercase">Explore Our Collections</h2>
            <p className="text-lg text-gray-500 mt-6 max-w-2xl mx-auto">
              Each collection tells a story. Discover pieces that resonate with your journey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Archive Collection */}
            <Card 
              className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group cursor-pointer overflow-hidden"
              onClick={() => window.location.href = '/collections/archive'}
            >
              <div className="relative overflow-hidden aspect-[4/5]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/ygs0x6wd_IMG_2794.JPG" 
                  alt="Archive Collection"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="product-overlay-streetwear">
                  <Button className="btn-primary-streetwear btn-lg">EXPLORE ARCHIVE</Button>
                </div>
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="bg-warmSage text-black font-black tracking-wider">VIEW COLLECTION</Button>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-black/80 text-warmSage text-xs font-black px-3 py-1 uppercase tracking-wider border border-warmSage/30">Archive</span>
                </div>
              </div>
              <CardContent className="p-8 bg-black border-t border-warmSage/30">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-3">ARCHIVE</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Classic pieces from our vault. Limited availability on timeless designs.
                </p>
                <Button size="sm" className="bg-warmSage text-black font-black hover:bg-white transition-colors">
                  VIEW COLLECTION <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* New from PRSVR */}
            <Card 
              className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group cursor-pointer overflow-hidden"
              onClick={() => window.location.href = '/collections/new-prsvr'}
            >
              <div className="relative overflow-hidden aspect-[4/5]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_prsvr-uniforms/artifacts/4q64g6me_IMG_3297%202.JPG" 
                  alt="New from PRSVR"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="bg-warmSage text-black font-black tracking-wider">SHOP NEW</Button>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-warmSage text-black text-xs font-black px-3 py-1 uppercase tracking-wider">New</span>
                </div>
              </div>
              <CardContent className="p-8 bg-black border-t border-warmSage/30">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-3">NEW FROM PRSVR</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Our latest drops. Fresh designs for those who keep going.
                </p>
                <Button size="sm" className="bg-warmSage text-black font-black hover:bg-white transition-colors">
                  VIEW COLLECTION <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Valley Leathers Collection */}
            <Card 
              className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group cursor-pointer overflow-hidden"
              onClick={() => window.location.href = '/collections/the-valley-collection'}
            >
              <div className="relative overflow-hidden aspect-[4/5]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/aeoxnjzq_IMG_2796.JPG" 
                  alt="Valley Leathers Collection"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="bg-warmSage text-black font-black tracking-wider">SHOP VALLEY LEATHERS</Button>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-black/80 text-warmSage text-xs font-black px-3 py-1 uppercase tracking-wider border border-warmSage/30">Valley Leathers</span>
                </div>
              </div>
              <CardContent className="p-8 bg-black border-t border-warmSage/30">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-3">VALLEY LEATHERS</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Premium leather pieces. Built for the streets, refined for luxury.
                </p>
                <Button size="sm" className="bg-warmSage text-black font-black hover:bg-white transition-colors">
                  VIEW COLLECTION <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Shop All Collections Button */}
          <div className="text-center">
            <Button 
              onClick={() => window.location.href = '/collections'}
              className="bg-warmSage text-black hover:bg-white font-black text-lg px-12 py-6 uppercase tracking-wider transition-colors"
            >
              SHOP ALL COLLECTIONS <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Email Signup */}
      <section id="contact" className="py-32 bg-warmSage text-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-black rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-black uppercase tracking-tight mb-4">READY TO<br/>PERSEVERE?</h2>
          <p className="text-xl text-black/70 font-medium">Join the community. Shop the culture.</p>
          
          {/* Email Signup Form */}
          <form onSubmit={handleNewsletterSignup} className="max-w-md mx-auto mt-12 mb-8">
            <div className="flex gap-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={subscribing}
                className="flex-1 bg-black text-white border-black focus:border-white h-14 px-6 text-lg rounded-none"
              />
              <Button 
                type="submit" 
                disabled={subscribing}
                className="bg-black text-white hover:bg-white hover:text-black px-8 h-14 font-black rounded-none disabled:opacity-50 transition-all"
              >
                <Mail className="mr-2 h-5 w-5" />
                {subscribing ? 'JOINING...' : 'JOIN'}
              </Button>
            </div>
          </form>

          <div className="flex flex-wrap justify-center gap-6 mt-12">
            <Button 
              size="lg" 
              className="bg-black text-white hover:bg-white hover:text-black px-12 py-6 text-lg font-black rounded-none transition-all"
              onClick={() => window.location.href = '#fashion'}
            >
              SHOP COLLECTIONS
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-black text-black hover:bg-black hover:text-white px-12 py-6 text-lg font-black rounded-none transition-all"
              onClick={() => window.location.href = 'mailto:info@prsvr.com'}
            >
              GET IN TOUCH
            </Button>
          </div>
        </div>
      </section>

      {/* PRSVR Sports */}
      <section id="sports" className="py-24 bg-black border-t border-warmSage/10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
            <div>
              <span className="text-warmSage font-bold text-sm tracking-[0.3em] uppercase mb-4 block">PRSVR SPORTS</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white uppercase">ATHLETIC PERFORMANCE</h2>
              <p className="text-gray-500 mt-2">Luxury Design for Champions</p>
            </div>
            <Button 
              className="bg-transparent border-2 border-warmSage text-warmSage font-black tracking-wider hover:bg-warmSage hover:text-black transition-all"
              onClick={() => window.location.href = '/uniform-order'}
            >
              ON COURT COLLECTION <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
          
          {/* Featured Products */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <Card className="bg-black border border-warmSage/20 hover:border-warmSage/50 transition-all duration-300 group cursor-pointer overflow-hidden" onClick={() => window.location.href = '/uniform-order'}>
              <div className="relative overflow-hidden bg-black" style={{height: '512px'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/tzvax5ni_IMG_3277%202.JPG" 
                  alt="Custom Uniforms"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button className="btn-outline-light">VIEW</Button>
                </div>
              </div>
              <CardContent className="p-6 bg-charcoal border-t-2 border-warmSage/20">
                <h3 className="product-title-sports">CUSTOM UNIFORMS</h3>
                <p className="text-warmGray text-sm mt-2">Full team customization</p>
              </CardContent>
            </Card>

            <Card className="product-card-sports group cursor-pointer">
              <div className="relative overflow-hidden bg-black" style={{height: '512px'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/ki87p191_IMG_3283.JPG" 
                  alt="Basketball Shorts"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
                <div className="product-overlay-dark">
                  <Button className="btn-outline-light">VIEW</Button>
                </div>
              </div>
              <CardContent className="p-6 bg-charcoal border-t-2 border-warmSage/20">
                <h3 className="product-title-sports">BASKETBALL SHORTS</h3>
                <p className="text-warmGray text-sm mt-2">Performance ready</p>
              </CardContent>
            </Card>

            <Card className="product-card-sports group cursor-pointer">
              <div className="relative overflow-hidden bg-black" style={{height: '512px'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/57ftfxhu_IMG_3282.JPG" 
                  alt="Weighted Basketball"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
                <div className="product-overlay-dark">
                  <Button className="btn-outline-light">VIEW</Button>
                </div>
              </div>
              <CardContent className="p-6 bg-charcoal border-t-2 border-warmSage/20">
                <h3 className="product-title-sports">WEIGHTED BASKETBALL</h3>
                <p className="text-warmGray text-sm mt-2">Train harder</p>
              </CardContent>
            </Card>

            <Card className="product-card-sports group cursor-pointer">
              <div className="relative overflow-hidden bg-black" style={{height: '512px'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/damt4661_IMG_3279.JPG" 
                  alt="Team Merch"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
                <div className="product-overlay-dark">
                  <Button className="btn-outline-light">VIEW</Button>
                </div>
              </div>
              <CardContent className="p-6 bg-charcoal border-t-2 border-warmSage/20">
                <h3 className="product-title-sports">TEAM MERCH</h3>
                <p className="text-warmGray text-sm mt-2">Build team spirit</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Call to Action Message */}
          <div className="text-center mt-12 p-8 bg-charcoal border-2 border-cream/20 rounded-lg">
            <h3 className="text-3xl font-black text-cream mb-4 uppercase">Built for Performance</h3>
            <p className="text-warmGray text-lg mb-6">
              Custom team uniforms designed for athletes who refuse to settle.
            </p>
            <Button 
              className="bg-cream text-charcoal hover:bg-cream/90 font-black px-8 py-4 text-lg"
              onClick={() => window.location.href = '/uniform-order'}
            >
              START YOUR ORDER
            </Button>
          </div>

        </div>
      </section>

      {/* Services Section - Expandable */}
      <section id="services" className="py-24 bg-cream">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="badge-dark mb-4">OUR NEW VERTICALS</Badge>
            <h2 className="section-title-streetwear">FASHION, FAMILY,<br/>AND BUSINESS</h2>
          </div>
          <Accordion type="single" collapsible className="max-w-4xl mx-auto">
            {verticals.slice(2).map((vertical, index) => (
              <AccordionItem key={vertical.id} value={`item-${index}`} className="mb-6">
                <AccordionTrigger className="accordion-trigger-streetwear">
                  <div>
                    <h3 className="text-2xl font-black text-charcoal text-left">{vertical.name}</h3>
                    <p className="text-sm font-bold text-warmSage uppercase tracking-wider mt-1 text-left">{vertical.tagline}</p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="accordion-content-streetwear">
                  <p className="text-warmGray mb-6 leading-relaxed">{vertical.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    {vertical.features.map((feature, idx) => (
                      <div key={idx} className="feature-item-vertical">
                        <Check className="h-5 w-5 text-warmSage mb-2" />
                        <span className="text-sm font-semibold text-charcoal">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="btn-dark mt-6 w-full">
                    LEARN MORE <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Testimonials - Fixed Alignment */}
      <section className="py-24 bg-charcoal">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="badge-streetwear mb-4">VERIFIED</Badge>
            <h2 className="section-title-streetwear text-cream">WHAT THEY SAY</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card-streetwear h-full">
                <CardContent className="p-10 flex flex-col h-full">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-warmSage text-warmSage" />
                    ))}
                  </div>
                  <p className="testimonial-quote-streetwear flex-1">"{testimonial.quote}"</p>
                  <div className="mt-8 pt-6 border-t border-warmSage/20">
                    <p className="testimonial-author-streetwear">{testimonial.author}</p>
                    <p className="testimonial-role-streetwear">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Button - Shows on leather/archive product pages */}
      <ContactButton />
    </div>
  );
};

export default Home;